/**
 * Market Simulator using Poisson process for realistic price movements.
 * JavaScript port of the Python backend simulator.
 * Generates OHLC (Open, High, Low, Close) candlestick data.
 */

class MarketSimulator {
  constructor({
    initialPrice = 4291.05,
    volatility = 0.012,
    drift = 0.0,
    lambdaParam = 2.5,
    meanReversion = 0.05,
    candleDuration = 10, // seconds
  } = {}) {
    this.currentPrice = initialPrice;
    this.initialPrice = initialPrice;
    this.volatility = volatility;
    this.drift = drift;
    this.lambdaParam = lambdaParam;
    this.meanReversion = meanReversion;
    this.time = 0;
    this.candleHistory = [];
    this.candleIdCounter = 0;

    // Live candle tracking
    this.liveCandleDuration = candleDuration;
    this.liveCandleStartTime = Date.now();
    this.liveCandleOpen = initialPrice;
    this.liveCandlePrices = [initialPrice];
  }

  /**
   * Generate Poisson-distributed random number.
   */
  _poissonRandom(lambda) {
    const L = Math.exp(-lambda);
    let k = 0;
    let p = 1.0;

    while (p > L) {
      k += 1;
      p *= Math.random();
    }

    return k - 1;
  }

  /**
   * Generate a Gaussian (normal) distributed random number using Box-Muller transform.
   */
  _gaussianRandom(mean = 0, stdDev = 1) {
    const u1 = Math.random();
    const u2 = Math.random();
    const z0 = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
    return z0 * stdDev + mean;
  }

  /**
   * Generate a price jump using Poisson process.
   */
  poissonJump() {
    // Number of jumps in this time interval (Poisson distributed)
    const numJumps = this._poissonRandom(this.lambdaParam * 0.1);

    let jumpSize = 0;
    for (let i = 0; i < numJumps; i++) {
      // Each jump has random direction (up or down) and size
      const direction = Math.random() > 0.5 ? 1 : -1;
      const magnitude = Math.abs(this._gaussianRandom(0, this.volatility));
      jumpSize += direction * magnitude;
    }

    return jumpSize;
  }

  /**
   * Generate next price using Poisson jump process with drift and mean reversion.
   */
  generatePriceMovement() {
    // Mean reversion component (pulls price back toward initial price)
    const priceDeviation = this.currentPrice - this.initialPrice;
    const meanReversionComponent = -this.meanReversion * priceDeviation * 0.001;

    // Drift component (trend)
    const driftComponent = this.drift * this.currentPrice;

    // Jump component (Poisson process - can be positive or negative)
    const jumpComponent = this.poissonJump() * this.currentPrice;

    // Random walk component (symmetric around 0)
    const randomWalk =
      this._gaussianRandom(0, this.volatility * 0.3) * this.currentPrice;

    // Combine all components
    const priceChange =
      meanReversionComponent + driftComponent + jumpComponent + randomWalk;

    this.currentPrice += priceChange;

    // Prevent price from going negative or too low
    if (this.currentPrice < this.initialPrice * 0.5) {
      this.currentPrice = this.initialPrice * 0.5;
    }

    this.time += 1;
    return this.currentPrice;
  }

  /**
   * Generate a single OHLC candlestick and add to history.
   */
  generateCandle(numTicks = 20) {
    const openPrice = this.currentPrice;
    const prices = [openPrice];

    // Generate multiple price movements for this candle
    for (let i = 0; i < numTicks; i++) {
      prices.push(this.generatePriceMovement());
    }

    const closePrice = prices[prices.length - 1];
    const highPrice = Math.max(...prices);
    const lowPrice = Math.min(...prices);

    // Generate volume (also using Poisson-like distribution)
    const baseVolume = 1000000;
    const volume = baseVolume + this._poissonRandom(500000);

    // Determine candle type
    const candleType = closePrice > openPrice ? "bull" : "bear";

    const candle = {
      open: Math.round(openPrice * 100) / 100,
      high: Math.round(highPrice * 100) / 100,
      low: Math.round(lowPrice * 100) / 100,
      close: Math.round(closePrice * 100) / 100,
      volume,
      type: candleType,
      timestamp: new Date().toISOString(),
      id: this.candleIdCounter,
      closed: true,
    };

    // Add to history and increment counter
    this.candleHistory.push(candle);
    this.candleIdCounter += 1;

    // Keep only last 100 candles in history
    if (this.candleHistory.length > 100) {
      this.candleHistory.shift();
    }

    return candle;
  }

  /**
   * Get the most recent candles from history.
   */
  getRecentCandles(count = 5) {
    // If we don't have enough candles, generate them
    while (this.candleHistory.length < count) {
      this.generateCandle();
    }

    // Return the last N candles
    return this.candleHistory.slice(-count);
  }

  /**
   * Close the current live candle and add to history.
   */
  _closeLiveCandle() {
    if (this.liveCandlePrices.length > 0) {
      const candle = {
        open: Math.round(this.liveCandleOpen * 100) / 100,
        high: Math.round(Math.max(...this.liveCandlePrices) * 100) / 100,
        low: Math.round(Math.min(...this.liveCandlePrices) * 100) / 100,
        close:
          Math.round(this.liveCandlePrices[this.liveCandlePrices.length - 1] * 100) / 100,
        volume: 1000000 + this._poissonRandom(500000),
        type:
          this.liveCandlePrices[this.liveCandlePrices.length - 1] > this.liveCandleOpen
            ? "bull"
            : "bear",
        timestamp: new Date(this.liveCandleStartTime).toISOString(),
        id: this.candleIdCounter,
        closed: true,
      };

      this.candleHistory.push(candle);
      this.candleIdCounter += 1;

      // Keep only last 100 candles
      if (this.candleHistory.length > 100) {
        this.candleHistory.shift();
      }
    }

    // Start new live candle
    this.liveCandleStartTime = Date.now();
    this.liveCandleOpen = this.currentPrice;
    this.liveCandlePrices = [this.currentPrice];
  }

  /**
   * Update the live candle with a new price tick.
   */
  updateLiveCandle() {
    // Check if current candle should close
    const elapsed = (Date.now() - this.liveCandleStartTime) / 1000;

    if (elapsed >= this.liveCandleDuration) {
      this._closeLiveCandle();
    }

    // Generate new price movement
    const newPrice = this.generatePriceMovement();
    this.liveCandlePrices.push(newPrice);

    return this.getLiveCandleData();
  }

  /**
   * Get the current state of the live candle.
   */
  getLiveCandleData() {
    if (this.liveCandlePrices.length === 0) {
      return null;
    }

    const elapsed = (Date.now() - this.liveCandleStartTime) / 1000;
    const progress = Math.min(elapsed / this.liveCandleDuration, 1.0);

    const currentClose = this.liveCandlePrices[this.liveCandlePrices.length - 1];

    return {
      open: Math.round(this.liveCandleOpen * 100) / 100,
      high: Math.round(Math.max(...this.liveCandlePrices) * 100) / 100,
      low: Math.round(Math.min(...this.liveCandlePrices) * 100) / 100,
      close: Math.round(currentClose * 100) / 100,
      volume: this.liveCandlePrices.length * 50000,
      type: currentClose > this.liveCandleOpen ? "bull" : "bear",
      timestamp: new Date().toISOString(),
      id: `live-${this.candleIdCounter}`,
      closed: false,
      progress: Math.round(progress * 1000) / 10, // Percentage with 1 decimal
      timeRemaining: Math.round((this.liveCandleDuration - elapsed) * 10) / 10,
    };
  }

  /**
   * Get recent closed candles plus the current live candle.
   */
  getCandlesWithLive(count = 4) {
    // Ensure we have enough history
    while (this.candleHistory.length < count) {
      this.generateCandle();
    }

    // Update live candle FIRST (this may close it and add to history)
    const liveCandle = this.updateLiveCandle();

    // THEN get the history (now includes just-closed candle if any)
    const closedCandles = this.candleHistory.slice(-count);

    return {
      closed: closedCandles,
      live: liveCandle,
      total: closedCandles.length + 1,
    };
  }

  /**
   * Get current market snapshot.
   */
  getCurrentMarketData() {
    const previousPrice = this.currentPrice;
    const currentPrice = this.generatePriceMovement();

    const priceChange = currentPrice - previousPrice;
    const percentChange = (priceChange / previousPrice) * 100;

    // Generate volume in billions
    const volume = Math.round((Math.random() * 2 + 1.5) * 10) / 10;

    return {
      price: Math.round(currentPrice * 100) / 100,
      change: Math.round(percentChange * 100) / 100,
      volume: `${volume}B`,
      timestamp: new Date().toISOString(),
      previousClose: Math.round(previousPrice * 100) / 100,
    };
  }

  /**
   * Reset simulator to initial state.
   */
  reset() {
    this.currentPrice = this.initialPrice;
    this.time = 0;
    this.candleHistory = [];
    this.candleIdCounter = 0;
    this.liveCandleStartTime = Date.now();
    this.liveCandleOpen = this.initialPrice;
    this.liveCandlePrices = [this.initialPrice];
  }
}

// Create singleton instance with default config
let simulatorInstance = null;

export const getSimulator = () => {
  if (!simulatorInstance) {
    simulatorInstance = new MarketSimulator({
      initialPrice: 4291.05,
      volatility: 0.012,
      drift: 0.0,
      lambdaParam: 2.5,
      meanReversion: 0.05,
    });
  }
  return simulatorInstance;
};

export default MarketSimulator;
